// ---------------------------------------------------------------------------
// Chart accessibility helpers — pure, no DOM
// ---------------------------------------------------------------------------
//
// Two concerns:
//  - chartTitleDesc: produces the <title>/<desc> markup embedded INSIDE the SVG
//    plus the ids to reference. It does NOT own role="img" — the consuming
//    wrapper element owns the image role + aria-labelledby (single owner).
//  - chartTable: a visually-hidden <table> rendered as a SIBLING outside the
//    img-role element (a role="img" parent flattens child semantics, so a table
//    nested inside it would be hidden from assistive tech).

import { svgText } from './svg-util.js';

/**
 * Build the SVG `<title>`/`<desc>` defs and the ids that reference them.
 *
 * @param {string} title
 * @param {string} desc
 * @param {string} idPrefix - unique per chart instance to avoid id collisions.
 * @returns {{ titleId: string, descId: string, svgDefs: string }}
 */
export function chartTitleDesc(title, desc, idPrefix) {
  const titleId = `${idPrefix}-title`;
  const descId = `${idPrefix}-desc`;
  const svgDefs =
    `<title id="${svgText(titleId)}">${svgText(title)}</title>` +
    `<desc id="${svgText(descId)}">${svgText(desc)}</desc>`;
  return { titleId, descId, svgDefs };
}

/**
 * Render a visually-hidden data table as the text alternative for a chart.
 * Returned as an HTML string; the card renderer inserts it as a SIBLING of the
 * `role="img"` chart media (never a descendant).
 *
 * @param {{ headers: string[], rows: (string|number)[][], caption?: string }} data
 * @returns {string}
 */
export function chartTable({ headers, rows, caption }) {
  const head = headers.map((h) => `<th scope="col">${svgText(h)}</th>`).join('');
  const body = rows
    .map((cells) => `<tr>${cells.map((c) => `<td>${svgText(c)}</td>`).join('')}</tr>`)
    .join('');
  const cap = caption ? `<caption>${svgText(caption)}</caption>` : '';
  return `<table class="sr-only">${cap}<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}
